import React, { useState, useRef, useEffect, MutableRefObject } from "react";
import VoiceChat from "./VoiceChat";
import styled from "styled-components";

import {
  ResultReason,
  SynthesisResult,
} from "microsoft-cognitiveservices-speech-sdk";

declare global {
  interface Window {
    ccOS: any;
  }
}

export type ChatType = {
  content: string;
  type: string;
  data: any;
  state: string; // init: 시작 / chat: 일반 / store: 매장 선택 / menu: 메뉴 선택 / pay: 결제중 / payment: 결제완료 / confirm: 다음 채팅 X
};

const speechsdk = require("microsoft-cognitiveservices-speech-sdk");

const Recorder: React.FC = () => {
  // STT / TTS
  const [chat, setChat] = useState<ChatType | null>(null); // 대화내용
  const [chatData, setChatData] = useState<any>(null); // 대화 data
  const [isLoading, setIsLoading] = useState<boolean>(false); // 데이터 로딩

  const [isChat, setIsChat] = useState<boolean>(false); // 채팅 여부
  const [recognizerMode, setRecognizerMode] = useState<boolean>(false); // 녹음 여부
  const [playIndex, setPlayIndex] = useState<number>(0); // data index

  const [isSpeaking, setIsSpeaking] = useState(false); // tts 재생 여부

  const [log, setLog] = useState<string>(""); // log
  const [lastTouchTime, setLastTouchTime] = useState<number>(0); // click Event

  const sttFromMic = async () => {
    setRecognizerMode(true);
    const speechConfig = speechsdk.SpeechConfig.fromSubscription(
      process.env.REACT_APP_AZURE_KEY,
      process.env.REACT_APP_AZURE_REGION
    );
    speechConfig.speechRecognitionLanguage = "ko-KR";
    speechConfig.setProperty(
      speechsdk.PropertyId.SpeechServiceConnection_InitialSilenceTimeoutMs,
      "8000"
    );

    const audioConfig = speechsdk.AudioConfig.fromDefaultMicrophoneInput();
    const recognizer = new speechsdk.SpeechRecognizer(
      speechConfig,
      audioConfig
    );

    recognizer.recognizeOnceAsync((result: any) => {
      setIsLoading(true);
      if (result.reason === ResultReason.RecognizedSpeech) {
        setChat({
          content: result.text,
          type: "user",
          data: null,
          state: chat !== null && chat.data !== null ? "chat" : "ai",
        });
      }
      setRecognizerMode(false);
      setIsLoading(false);
      recognizer.close();
    });
  };

  const playChatTTS = () => {
    if (chat !== null) {
      setIsSpeaking(true);

      const speechConfig = speechsdk.SpeechConfig.fromSubscription(
        process.env.REACT_APP_AZURE_KEY,
        process.env.REACT_APP_AZURE_REGION
      );
      speechConfig.speechSynthesisVoiceName = "ko-KR-YuJinNeural";

      const audioConfig = speechsdk.AudioConfig.fromDefaultSpeakerOutput();
      const synthesizer = new speechsdk.SpeechSynthesizer(
        speechConfig,
        audioConfig
      );

      const ssml = `
      <speak version="1.0" xmlns="http://www.w3.org/2001/10/synthesis" xml:lang="en-US">
        <voice name="${speechConfig.speechSynthesisVoiceName}">
          <prosody rate="1.2">
            ${chat.content}
          </prosody>
        </voice>
      </speak>
    `;

      synthesizer.speakSsmlAsync(
        ssml,
        (result: SynthesisResult) => {
          console.log("result", result);
          if (
            result.reason === speechsdk.ResultReason.SynthesizingAudioCompleted
          ) {
            console.log("end");
            onChatAudioEnded();
            setIsSpeaking(false);
            synthesizer.close();
          }
          // setIsSpeaking(false);
          // synthesizer.close();
        },
        (error: string) => {
          console.error(error);
          setIsSpeaking(false);
          synthesizer.close();
        }
      );
    }
  };

  const handleClick = () => {
    const currentTime = new Date().getTime();
    if (currentTime - lastTouchTime > 300) {
      resetSearchBar();
      return;
    } else {
      onClickSearchBar();
    }
    setLastTouchTime(0);
  };

  const clearAudio = () => {
    // if ("speechSynthesis" in window) {
    //   window.speechSynthesis.cancel();
    // }
    setIsSpeaking(false);
  };

  const resetSearchBar = () => {
    console.log("reset");
    setChat(null);
    setChatData(null);
    setPlayIndex(0);
    setIsSpeaking(false);
    setIsLoading(false);
    setRecognizerMode(false);
    setIsChat(true);
    clearAudio();
    setChat({
      content: "무엇을 도와 드릴까요?",
      type: "bot",
      data: null,
      state: "init",
    });
  };

  const onClickSearchBar = () => {
    clearAudio();
    setRecognizerMode(false);
    setPlayIndex(0);
    setRecognizerMode(true);
  };

  // chat
  const hasChatResult = async () => {
    if (chat !== null) {
      if (chat.type === "bot" || chat.state === "init") {
        console.log("chat", chat);
        clearAudio();
        playChatTTS();
      }
    } else {
      clearAudio();
    }
  };

  // const playChatTTS = () => {
  //   if ("speechSynthesis" in window && chat !== null) {
  //     const utterance = new SpeechSynthesisUtterance(chat.content);
  //     utterance.onend = () => {
  //       onChatAudioEnded();
  //     };
  //     const voices = window.speechSynthesis.getVoices();
  //     console.log(
  //       "voices",
  //       // voices.filter((item) => item.lang === "ko-KR")
  //       voices
  //     );
  //     const voice = voices.find((item) => item.name.includes("한국"));
  //     if (voice) {
  //       utterance.voice = voice;
  //     }

  //     window.speechSynthesis.speak(utterance);
  //   }
  // };

  const onChatAudioEnded = () => {
    if (chat !== null) {
      if (
        chat.state !== "confirm" &&
        chat.state !== "pay" &&
        chat.state !== "payment"
      ) {
        setRecognizerMode(true);
      }
    } else {
      clearAudio();
    }
  };

  useEffect(() => {
    hasChatResult();
  }, [chat]);

  useEffect(() => {
    if (!isChat) {
      setChat(null);
      setRecognizerMode(false);
      setPlayIndex(0);
      clearAudio();
    }
  }, [isChat]);

  useEffect(() => {
    if (recognizerMode) {
      clearAudio();
      setLog("vr start~~~~~~~~~~");
      sttFromMic();
    } else {
      setLog("vr stop~~~~~~~~~~~");
      // stopRecording();
    }
  }, [recognizerMode]);

  return (
    <Style>
      {/* <div className="stt">
        <br />
        <br />
        <br />
        <h1>text: {JSON.stringify(chat)}</h1>
      </div> */}
      <VoiceChat
        chat={chat}
        setChat={setChat}
        recognizerMode={recognizerMode}
        setIsChat={setIsChat}
        chatData={chatData}
        setChatData={setChatData}
        setPlayIndex={setPlayIndex}
        isLoading={isLoading}
        setIsLoading={setIsLoading}
      />
      <button
        className="voice stt"
        onTouchStart={() => setLastTouchTime(new Date().getTime())}
        onTouchEnd={handleClick}
        // onClick={sttFromMic}
      >
        <img
          src={`${process.env.PUBLIC_URL}/svg/ic_voice.svg`}
          alt="음성인식 아이콘"
          width="100%"
          height="100%"
        />
      </button>
    </Style>
  );
};

export default Recorder;

const Style = styled.div`
  div.stt {
    position: absolute;
    top: 10px;
    left: 10px;
    z-index: 1000000000;

    h1 {
      color: red;
    }
  }

  button.voice.stt {
    z-index: 100;
    height: 16%;
    aspect-ratio: 1/1;
    border-radius: 100%;
    position: fixed;
    right: 40px;
    bottom: 40px;
    background: linear-gradient(180deg, #fb69ef 0%, #2a5ec4 86%);

    img {
      width: 50%;
      height: 50%;
    }
  }

  @media (max-width: 1400px) {
    button.voice.stt {
      width: 16%;
      height: auto;
    }
  }
`;
